// 8-handle resize affordance for the single-selected shape.
//
// Renders inside the main Konva Layer so the handles share the world-space
// transform with everything else. The visible square is sized in screen
// pixels (divided by viewport.scale) so the handle stays the same physical
// size at any zoom level; the hit-target is wider than the visual so the
// user doesn't have to be pixel-perfect with the pointer.
//
// Each handle carries name="resize-handle" and id=<handle id> on its hit
// rect so the Canvas mouse-down handler can identify it via the same
// parent-walk pattern that findShapeId uses for shapes.

import { Group, Rect as KRect } from 'react-konva';
import type { CanvasTokens } from '../styles/tokens';
import { cursorForHandle, handleCenter, isCornerHandle, RESIZE_HANDLES } from './resize';

interface Props {
  // World-space bbox of the selected shape.
  bbox: { x: number; y: number; w: number; h: number };
  scale: number;
  tokens: CanvasTokens;
  onHover: (cursor: string | null) => void;
  // Handle layout mode. 'all' = 8 handles (default rects/ellipses/frames).
  // 'sticky' = corners + left/right edges only — N/S edges would let the
  // user squash the sticky vertically and break the paper-on-paper feel.
  layout?: 'all' | 'sticky';
}

const VISIBLE_SIZE = 8;  // screen px
const HIT_SIZE = 12;     // screen px — slightly larger so handles are forgiving
const RING_OFFSET = 2;   // matches SelectionRing offset so handles sit on the ring

export function SelectionHandles({ bbox, scale, tokens, onHover, layout = 'all' }: Props) {
  const v = VISIBLE_SIZE / scale;
  const hit = HIT_SIZE / scale;
  const ring = RING_OFFSET / scale;
  const handles =
    layout === 'sticky'
      ? RESIZE_HANDLES.filter((h) => isCornerHandle(h) || h === 'e' || h === 'w')
      : RESIZE_HANDLES;

  // bbox is in world coords already aligned with the shape's group; render
  // handles as a sibling translated to the shape's origin.
  return (
    <Group x={bbox.x} y={bbox.y} listening>
      {handles.map((handle) => {
        const center = handleCenter(handle, bbox.w, bbox.h);
        const visibleX = center.x - v / 2;
        const visibleY = center.y - v / 2;
        const hitX = center.x - hit / 2;
        const hitY = center.y - hit / 2;
        // Nudge corner+edge handles outward by RING_OFFSET so they sit on
        // top of the selection ring rather than inside the shape body.
        const outwardX = handle.includes('e') ? ring : handle.includes('w') ? -ring : 0;
        const outwardY = handle.includes('s') ? ring : handle.includes('n') ? -ring : 0;
        return (
          <Group key={handle} x={outwardX} y={outwardY}>
            <KRect
              x={visibleX}
              y={visibleY}
              width={v}
              height={v}
              fill={tokens.bgApp}
              stroke={tokens.selection}
              strokeWidth={1.5 / scale}
              cornerRadius={1 / scale}
              listening={false}
            />
            <KRect
              x={hitX}
              y={hitY}
              width={hit}
              height={hit}
              fill="transparent"
              name="resize-handle"
              id={handle}
              onMouseEnter={() => onHover(cursorForHandle(handle))}
              onMouseLeave={() => onHover(null)}
            />
          </Group>
        );
      })}
    </Group>
  );
}
